import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ActivityFeed from "@/components/app/ActivityFeed";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import SimpleMD from "@/components/app/SimpleMD";
import WorkflowWizard from "@/components/app/WorkflowWizard";
import { usePresence } from "@/lib/presence-context";
import ProjectPulseCard from "@/components/app/ProjectPulseCard";
import CustomFieldsPanel from "@/components/app/CustomFieldsPanel";
import PmpTemplatePicker from "@/components/app/PmpTemplatePicker";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from "recharts";
import {
  Sparkles, KanbanSquare, ShieldAlert, FileText, Plus, Pencil, Trash2,
  Save, X, ClipboardList, BarChart3, Target, Link2, GanttChartSquare, MessageSquare, BookOpen, Brain, CheckSquare, Share2, Copy, Activity, ChevronDown, ChevronUp, Loader2, DollarSign, MapPin, GitPullRequest, Download,
} from "lucide-react";
import { toast } from "sonner";

const titleCase = (s) =>
  (s || "").replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());

const AI_DOC_TYPES = [
  { key: "charter", name: "Project Charter" },
  { key: "status_report", name: "Status Report" },
  { key: "risk_register", name: "Risk Register" },
  { key: "meeting_agenda", name: "Meeting Agenda" },
  { key: "scope_statement", name: "Scope Statement" },
];

const ProjectDetail = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("charter");
  const [generating, setGenerating] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", content: "" });
  const [openManual, setOpenManual] = useState(false);
  const [manualForm, setManualForm] = useState({ title: "", content: "", doc_type: "custom" });
  const [forecastBusy, setForecastBusy] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [closeOutOpen, setCloseOutOpen] = useState(false);
  const [closeOutBusy, setCloseOutBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [portalToken, setPortalToken] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [forecastHistory, setForecastHistory] = useState([]);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickForm, setQuickForm] = useState({ title: "", priority: "medium", item_type: "task" });
  const [quickBusy, setQuickBusy] = useState(false);
  const [budget, setBudget] = useState(null);
  const [budgetExpanded, setBudgetExpanded] = useState(false);
  const [activityExpanded, setActivityExpanded] = useState(false);
  const [roadmapExpanded, setRoadmapExpanded] = useState(false);
  const [milestones, setMilestones] = useState([]);
  const [pmpDocs, setPmpDocs] = useState([]);
  const [pmpPickerOpen, setPmpPickerOpen] = useState(false);
  const [pmpSelected, setPmpSelected] = useState([]);
  const [pmpBusy, setPmpBusy] = useState(false);
  const [delivery, setDelivery] = useState(null);

  const [closeForm, setCloseForm] = useState({
    final_contract_value: "",
    final_cost: "",
    final_labour_cost: "",
    final_nonlabour_cost: "",
    final_margin: "",
    target_margin: "",
    days_over_schedule: "",
    total_scope_changes: "",
    total_change_value: "",
    overrun_cause: "",
  });

  const loadAll = async () => {
    const [p, d, oc, pt] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/governance-documents?project_id=${projectId}`),
      api.get(`/projects/${projectId}/outcome`).catch(() => ({ data: null })),
      api.get(`/portal/projects/${projectId}/token-status`).catch(() => ({ data: { enabled: false } })),
    ]);
    setProject(p.data);
    setDocs(d.data);
    setOutcome(oc.data);
    setPortalToken(pt.data.enabled ? pt.data.token : null);
    if (d.data[0]) setSelectedDoc(d.data[0]);
    // Pre-fill close-out form from project data
    setCloseForm((f) => ({
      ...f,
      final_contract_value: p.data.contract_value || "",
      target_margin: p.data.target_margin_percentage || "",
    }));
  };

  useEffect(() => { loadAll(); loadForecastHistory(); loadBudget(); loadMilestones(); loadPmpDocs(); loadDelivery(); }, [projectId]); // eslint-disable-line

  const loadDelivery = async () => {
    try {
      const { data } = await api.get(`/intelligence/projects/${projectId}/delivery-forecast`);
      setDelivery(data);
    } catch { /* silent — panel just stays hidden */ }
  };

  const loadPmpDocs = async () => {
    try {
      const { data } = await api.get(`/documents?project_id=${projectId}&category=pmp`);
      setPmpDocs(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  };

  const generatePmpDocs = async () => {
    if (pmpSelected.length === 0) return toast.error("Select at least one template");
    setPmpBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/pmp-documents`, { titles: pmpSelected });
      toast.success(`Generated ${data.documents_generated} document${data.documents_generated === 1 ? "" : "s"}`);
      setPmpSelected([]);
      setPmpPickerOpen(false);
      loadPmpDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate documents");
    } finally {
      setPmpBusy(false);
    }
  };

  const downloadPmpDoc = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.doc_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename || doc.name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const deletePmpDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    try {
      await api.delete(`/documents/${doc.doc_id}`);
      setPmpDocs((prev) => prev.filter((d) => d.doc_id !== doc.doc_id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const loadBudget = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/budget`);
      setBudget(data);
    } catch { /* silent */ }
  };

  const loadMilestones = async () => {
    try {
      const { data } = await api.get(`/tasks?project_id=${projectId}&item_type=milestone&limit=50`);
      const ms = (Array.isArray(data) ? data : data.items || [])
        .filter((t) => t.due_date)
        .sort((a, b) => a.due_date.localeCompare(b.due_date));
      setMilestones(ms);
    } catch { /* silent */ }
  };

  const loadForecastHistory = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/forecast-history`);
      const pts = (data.snapshots || [])
        .filter((s) => s.predicted_final_margin != null)
        .map((s) => ({
          date: s.created_at ? s.created_at.slice(0, 10) : "—",
          margin: parseFloat((s.predicted_final_margin ?? 0).toFixed(1)),
          cost: Math.round(s.predicted_final_cost || 0),
        }))
        .sort((a, b) => a.date.localeCompare(b.date));
      setForecastHistory(pts);
    } catch {
      // silent — chart just stays hidden
    }
  };

  const quickAddTask = async () => {
    if (!quickForm.title.trim()) return;
    setQuickBusy(true);
    try {
      await api.post("/tasks", {
        project_id: projectId,
        title: quickForm.title.trim(),
        priority: quickForm.priority,
        item_type: quickForm.item_type,
        status: "todo",
      });
      toast.success("Task added");
      setQuickForm({ title: "", priority: "medium", item_type: "task" });
      setQuickAddOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add task");
    } finally {
      setQuickBusy(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/generate-doc", { project_id: projectId, doc_type: docType });
      toast.success("Document generated");
      setDocs([data, ...docs]);
      setSelectedDoc(data);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate document");
    } finally {
      setGenerating(false);
    }
  };

  const createManual = async () => {
    if (!manualForm.title.trim()) return;
    const { data } = await api.post("/governance-documents", { project_id: projectId, ...manualForm });
    setDocs([data, ...docs]);
    setSelectedDoc(data);
    setOpenManual(false);
    setManualForm({ title: "", content: "", doc_type: "custom" });
    toast.success("Document created");
  };

  const startEdit = () => {
    if (!selectedDoc) return;
    setEditForm({ title: selectedDoc.title, content: selectedDoc.content });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!selectedDoc) return;
    const { data } = await api.patch(`/governance-documents/${selectedDoc.doc_id}`, editForm);
    setSelectedDoc(data);
    setDocs(docs.map((d) => (d.doc_id === data.doc_id ? data : d)));
    setEditing(false);
    toast.success("Saved");
  };

  const removeDoc = async () => {
    if (!selectedDoc) return;
    await api.delete(`/governance-documents/${selectedDoc.doc_id}`);
    const next = docs.filter((d) => d.doc_id !== selectedDoc.doc_id);
    setDocs(next);
    setSelectedDoc(next[0] || null);
    setEditing(false);
    toast.success("Deleted");
  };

  const updateField = async (field, value) => {
    const { data } = await api.patch(`/projects/${projectId}`, { [field]: value });
    setProject(data);
    toast.success("Updated");
  };

  const openCloseOut = async () => {
    // Pull live cost totals from burndown to pre-fill
    try {
      const { data: bd } = await api.get(`/projects/${projectId}/burndown`);
      const labour = bd.total_cash_burned - (bd.total_expenses_burned || 0);
      const nonlabour = bd.total_expenses_burned || 0;
      const totalCost = bd.total_cash_burned || 0;
      setCloseForm((f) => ({
        ...f,
        final_cost: totalCost.toFixed(2),
        final_labour_cost: labour.toFixed(2),
        final_nonlabour_cost: nonlabour.toFixed(2),
      }));
    } catch {}
    setCloseOutOpen(true);
  };

  const computeMargin = () => {
    const cv = parseFloat(closeForm.final_contract_value) || 0;
    const cost = parseFloat(closeForm.final_cost) || 0;
    if (cv > 0) {
      const m = ((cv - cost) / cv * 100).toFixed(1);
      setCloseForm((f) => ({ ...f, final_margin: m }));
    }
  };

  const submitCloseOut = async () => {
    if (!closeForm.final_contract_value || !closeForm.final_cost) {
      toast.error("Contract value and final cost are required");
      return;
    }
    setCloseOutBusy(true);
    try {
      const payload = {
        final_contract_value: parseFloat(closeForm.final_contract_value),
        final_cost: parseFloat(closeForm.final_cost),
        final_labour_cost: parseFloat(closeForm.final_labour_cost) || 0,
        final_nonlabour_cost: parseFloat(closeForm.final_nonlabour_cost) || 0,
        final_margin: parseFloat(closeForm.final_margin) || 0,
        target_margin: parseFloat(closeForm.target_margin) || 0,
        ...(closeForm.days_over_schedule !== "" && { days_over_schedule: parseInt(closeForm.days_over_schedule) }),
        ...(closeForm.total_scope_changes !== "" && { total_scope_changes: parseInt(closeForm.total_scope_changes) }),
        ...(closeForm.total_change_value !== "" && { total_change_value: parseFloat(closeForm.total_change_value) }),
        ...(closeForm.overrun_cause && closeForm.overrun_cause !== "none" && { overrun_cause: closeForm.overrun_cause }),
      };
      const { data } = await api.post(`/projects/${projectId}/outcome`, payload);
      setOutcome(data);
      setCloseOutOpen(false);
      toast.success("Project closed out — outcome recorded");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Close-out failed");
    } finally {
      setCloseOutBusy(false);
    }
  };

  const runForecast = async () => {
    setForecastBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/forecast`);
      setForecast(data);
      const pred = data.prediction || {};
      if (pred.status === "insufficient_data") {
        toast.info("Forecast needs ≥ 2 weekly snapshots — captured one now. Run again next week.");
      } else if (pred.status === "no_contract_value") {
        toast.error("Set contract value & target margin first.");
      } else if (pred.is_margin_at_risk) {
        toast.error(`Margin fade detected: forecast ${pred.predicted_final_margin}% vs target ${project.target_margin_percentage}%`);
      } else {
        toast.success(`Forecast healthy: ${pred.predicted_final_margin}% margin trajectory.`);
      }
      // Refresh history chart so new snapshot shows immediately
      loadForecastHistory();
      setHistoryExpanded(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Forecast failed");
    } finally {
      setForecastBusy(false);
    }
  };

  if (!project) return <div className="p-12 text-sm text-[#6B7280]">Loading…</div>;

  return (
    <div>
      <PageHeader
        eyebrow={
          <ProjectPresenceEyebrow methodology={project.methodology} projectId={projectId} />
        }
        title={project.name}
        description={project.description || "No description"}
        actions={
          <div className="flex flex-wrap gap-2">
            <WorkflowWizard projectId={projectId} projectName={project.name} project={project} />
            <Link to={`/workspace/${projectId}/board`}>
              <Button variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="open-board-btn">
                <KanbanSquare className="w-4 h-4 mr-1" /> Board
              </Button>
            </Link>
            <Link to={`/workspace/${projectId}/raid`}>
              <Button variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="open-raid-btn">
                <ShieldAlert className="w-4 h-4 mr-1" /> RAID
              </Button>
            </Link>
            <Link to={`/workspace/${projectId}/meetings`}>
              <Button variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="open-meetings-btn">
                <ClipboardList className="w-4 h-4 mr-1" /> Meetings
              </Button>
            </Link>
            <Link to={`/workspace/${projectId}/sprints`}>
              <Button variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="open-sprints-btn">
                <Target className="w-4 h-4 mr-1" /> Sprints
              </Button>
            </Link>
            <Link to={`/workspace/${projectId}/dependencies`}>
              <Button variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="open-deps-btn">
                <Link2 className="w-4 h-4 mr-1" /> Dependencies
              </Button>
            </Link>
            <Link to={`/workspace/${projectId}/gantt`}>
              <Button variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="open-gantt-btn">
                <GanttChartSquare className="w-4 h-4 mr-1" /> Gantt
              </Button>
            </Link>
            <Link to={`/workspace/${projectId}/chat`}>
              <Button variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="open-chat-btn">
                <MessageSquare className="w-4 h-4 mr-1" /> Chat
              </Button>
            </Link>
            <Link to={`/workspace/${projectId}/wiki`}>
              <Button variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="open-wiki-btn">
                <BookOpen className="w-4 h-4 mr-1" /> Wiki
              </Button>
            </Link>
            <Link to={`/workspace/${projectId}/standup`}>
              <Button variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="open-standup-btn">
                <Activity className="w-4 h-4 mr-1" /> Check-in
              </Button>
            </Link>
            <Button
              onClick={runForecast}
              disabled={forecastBusy}
              variant="outline"
              className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent"
              data-testid="run-forecast-btn"
            >
              <Brain className={`w-4 h-4 mr-1 ${forecastBusy ? "animate-pulse" : ""}`} />
              {forecastBusy ? "Forecasting…" : "Run Forecast"}
            </Button>
            <Link to={`/workspace/${projectId}/change-requests`}>
              <Button variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="open-cr-btn">
                <GitPullRequest className="w-4 h-4 mr-1" /> Changes
              </Button>
            </Link>
            <Link to={`/workspace/${projectId}/report`}>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="open-report-btn">
                <BarChart3 className="w-4 h-4 mr-1" /> Report
              </Button>
            </Link>
            {/* Client portal share */}
            {portalToken ? (
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-none border-[#4F46E5]/40 text-[#4F46E5] hover:bg-[#4F46E5]/10 text-xs h-8"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/client/${portalToken}`);
                    toast.success("Portal link copied");
                  }}
                  data-testid="copy-portal-link-btn"
                >
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy portal link
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-none text-[#6B7280] hover:text-[#FF007A] text-xs h-8"
                  onClick={async () => {
                    setPortalBusy(true);
                    try { await api.post(`/portal/projects/${projectId}/disable`); setPortalToken(null); toast.success("Portal disabled"); }
                    catch { toast.error("Failed"); }
                    finally { setPortalBusy(false); }
                  }}
                  disabled={portalBusy}
                  data-testid="disable-portal-btn"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 text-xs h-8"
                onClick={async () => {
                  setPortalBusy(true);
                  try {
                    const { data } = await api.post(`/portal/projects/${projectId}/enable`);
                    setPortalToken(data.portal_token);
                    navigator.clipboard.writeText(`${window.location.origin}/client/${data.portal_token}`);
                    toast.success("Portal enabled — link copied");
                  } catch { toast.error("Failed"); }
                  finally { setPortalBusy(false); }
                }}
                disabled={portalBusy}
                data-testid="enable-portal-btn"
              >
                <Share2 className="w-3.5 h-3.5 mr-1" /> Share with client
              </Button>
            )}
            {outcome ? (
              <Button
                variant="outline"
                className="rounded-none border-[#00F5D4]/40 text-[#00F5D4] bg-[#00F5D4]/5 cursor-default"
                data-testid="closeout-done-btn"
              >
                <CheckSquare className="w-4 h-4 mr-1" /> Closed out
              </Button>
            ) : (
              <Button
                onClick={openCloseOut}
                variant="outline"
                className="rounded-none border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10"
                data-testid="closeout-btn"
              >
                <CheckSquare className="w-4 h-4 mr-1" /> Close out
              </Button>
            )}
          </div>
        }
      />

      <div className="px-5 sm:px-8 lg:px-12 py-8 space-y-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1F2937] border border-[#1F2937]">
          {[
            { l: "Methodology", k: "methodology", opts: ["agile", "waterfall", "hybrid"] },
            { l: "Status", k: "status", opts: ["planning", "active", "on_hold", "completed"] },
          ].map((m) => (
            <div key={m.k} className="bg-[#0D1117] p-6">
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280]">{m.l}</div>
              <Select value={project[m.k]} onValueChange={(v) => updateField(m.k, v)}>
                <SelectTrigger className="rounded-none mt-2 border-0 px-0 h-auto font-display font-bold text-xl tracking-tight" data-testid={`project-${m.k}-select`}>
                  <SelectValue>{titleCase(project[m.k])}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {m.opts.map((o) => <SelectItem key={o} value={o}>{titleCase(o)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
          <div className="bg-[#0D1117] p-6" data-testid="project-health-readout">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280]" title="Auto-computed from open critical RAID items, overdue tasks, and margin risk — not manually editable">
              Health
            </div>
            <div className="mt-2 flex items-center gap-2 font-display font-bold text-xl tracking-tight" data-testid="project-health-value">
              <span style={{
                width: 12, height: 12, borderRadius: "50%", flexShrink: 0,
                background: project.health === "green" ? "#22c55e" : project.health === "amber" ? "#f59e0b" : "#ef4444",
                boxShadow: project.health === "green" ? "0 0 6px #22c55e" : project.health === "amber" ? "0 0 6px #f59e0b" : "0 0 6px #ef4444",
              }} />
              {project.health === "green" ? "Green" : project.health === "amber" ? "Amber" : "Red"}
            </div>
            {((project.critical_raid_count || 0) > 0 || (project.overdue_tasks_count || 0) > 0) && (
              <div className="mt-1 text-[11px] text-[#6B7280]" data-testid="project-health-reason">
                {[
                  project.critical_raid_count > 0 && `${project.critical_raid_count} critical RAID`,
                  project.overdue_tasks_count > 0 && `${project.overdue_tasks_count} overdue task${project.overdue_tasks_count > 1 ? "s" : ""}`,
                ].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
          <div className="bg-[#0D1117] p-6">
            <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500">Progress</div>
            <div className="font-display font-black text-3xl tracking-tighter mt-2 text-[#002FA7]">{project.progress || 0}%</div>
          </div>
        </div>

        {/* Daily pulse (frictionless mobile taps + ideal-burn variance) */}
        <ProjectPulseCard projectId={projectId} />

        {delivery && (
          <div
            className={`border ${delivery.delivery.risk === "critical" || delivery.delivery.risk === "high" ? "border-rose-500" : delivery.delivery.risk === "medium" ? "border-amber-500" : "border-emerald-500"} bg-[#0D1117]`}
            data-testid="delivery-forecast-panel"
          >
            <div className="h-12 flex items-center gap-2 border-b border-[#1F2937] px-4 text-sm font-semibold">
              <Target className="w-4 h-4 text-[#002FA7]" /> Delivery &amp; Capacity Forecast
            </div>
            <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <ForecastStat
                label="On-time Probability"
                value={`${delivery.delivery.on_time_probability}%`}
                accent={delivery.delivery.risk === "critical" || delivery.delivery.risk === "high" ? "text-rose-600" : delivery.delivery.risk === "medium" ? "text-amber-500" : "text-emerald-600"}
              />
              <ForecastStat label="Schedule Risk" value={titleCase(delivery.delivery.risk)} />
              <ForecastStat label="Overdue Tasks" value={delivery.delivery.overdue_tasks} accent={delivery.delivery.overdue_tasks > 0 ? "text-rose-600" : "text-emerald-600"} />
              <ForecastStat
                label="Capacity"
                value={`${delivery.capacity.utilization_pct}%`}
                accent={delivery.capacity.status === "over" ? "text-rose-600" : delivery.capacity.status === "tight" ? "text-amber-500" : "text-emerald-600"}
              />
            </div>
          </div>
        )}

        {forecast && (
          <div
            className={`border ${forecast.prediction?.is_margin_at_risk ? "border-rose-500" : forecast.prediction?.status === "ok" ? "border-emerald-500" : "border-slate-400"} bg-[#0D1117]`}
            data-testid="forecast-result-panel"
          >
            <div
              className={`h-12 flex items-center justify-between border-b ${forecast.prediction?.is_margin_at_risk ? "border-rose-500 bg-rose-500" : forecast.prediction?.status === "ok" ? "border-emerald-500 bg-emerald-600" : "border-slate-400 bg-slate-600"} text-white px-4`}
            >
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Brain className="w-4 h-4" /> AI Predictive Margin Forecast
              </div>
              <button
                onClick={() => setForecast(null)}
                className="text-white/80 hover:text-white"
                data-testid="dismiss-forecast-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-3">
              {forecast.prediction?.status === "insufficient_data" ? (
                <div className="text-sm text-slate-300">
                  Snapshot captured. The regression needs at least 2 weekly snapshots — run again next week to see a trajectory.
                </div>
              ) : forecast.prediction?.status === "no_contract_value" ? (
                <div className="text-sm text-red-700">Set contract value &amp; target margin in project settings before forecasting.</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <ForecastStat label="Forecast Margin" value={`${forecast.prediction.predicted_final_margin}%`} accent={forecast.prediction.is_margin_at_risk ? "text-rose-600" : "text-emerald-600"} />
                    <ForecastStat label="Forecast Final Cost" value={`£${(forecast.prediction.predicted_final_cost || 0).toLocaleString()}`} />
                    <ForecastStat label="Severity" value={forecast.prediction.risk_severity} accent={forecast.prediction.is_margin_at_risk ? "text-rose-600" : "text-emerald-600"} />
                    <ForecastStat label="Data Points" value={forecast.data_points} />
                  </div>
                  {forecast.alert && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded" data-testid="forecast-alert-message">
                      {forecast.alert.message}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Quick-add task bar ─────────────────────────────────────── */}
        <div className="border border-[#1F2937] bg-[#0D1117]" data-testid="quick-add-task-section">
          <button
            onClick={() => setQuickAddOpen((v) => !v)}
            className="w-full h-11 flex items-center justify-between px-4 text-xs uppercase tracking-[0.25em] text-slate-400 hover:text-white hover:bg-[#111827] transition-colors"
            data-testid="toggle-quick-add"
          >
            <div className="flex items-center gap-2">
              <Plus className="w-3.5 h-3.5" /> Quick-add task
            </div>
            {quickAddOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {quickAddOpen && (
            <div className="border-t border-[#1F2937] px-4 py-4 flex flex-wrap gap-3 items-end" data-testid="quick-add-form">
              <div className="flex-1 min-w-[180px]">
                <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Title</label>
                <input
                  autoFocus
                  value={quickForm.title}
                  onChange={(e) => setQuickForm({ ...quickForm, title: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") quickAddTask(); }}
                  placeholder="Task title…"
                  className="mt-1 w-full bg-[#030712] border border-[#1F2937] text-sm text-white px-3 py-2 rounded-md placeholder:text-slate-600 focus:outline-none focus:border-[#00F5D4]/40"
                  data-testid="quick-add-title"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Type</label>
                <Select value={quickForm.item_type} onValueChange={(v) => setQuickForm({ ...quickForm, item_type: v })}>
                  <SelectTrigger className="mt-1 h-9 w-28 bg-[#030712] border-[#1F2937] text-white text-sm rounded-md" data-testid="quick-add-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1F2937]">
                    {["task","story","bug","epic","milestone","phase"].map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Priority</label>
                <Select value={quickForm.priority} onValueChange={(v) => setQuickForm({ ...quickForm, priority: v })}>
                  <SelectTrigger className="mt-1 h-9 w-28 bg-[#030712] border-[#1F2937] text-white text-sm rounded-md" data-testid="quick-add-priority"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1F2937]">
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <button
                onClick={quickAddTask}
                disabled={quickBusy || !quickForm.title.trim()}
                className="h-9 px-4 bg-[#00F5D4]/10 hover:bg-[#00F5D4]/20 border border-[#00F5D4]/30 text-[#00F5D4] text-sm font-mono rounded-md transition disabled:opacity-50 flex items-center gap-2"
                data-testid="quick-add-submit"
              >
                {quickBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Add
              </button>
              <Link to={`/workspace/${projectId}/board`} className="h-9 px-3 flex items-center text-xs text-slate-500 hover:text-white transition">
                Open board →
              </Link>
            </div>
          )}
        </div>

        {/* ── Forecast trajectory chart ──────────────────────────────── */}
        {forecastHistory.length >= 2 && (
          <div className="border border-[#1F2937] bg-[#0D1117]" data-testid="forecast-history-chart">
            <button
              onClick={() => setHistoryExpanded((v) => !v)}
              className="w-full h-11 flex items-center justify-between px-4 text-xs uppercase tracking-[0.25em] text-slate-400 hover:text-white hover:bg-[#111827] transition-colors"
            >
              <div className="flex items-center gap-2">
                <Brain className="w-3.5 h-3.5 text-[#002FA7]" /> Margin forecast trajectory · {forecastHistory.length} snapshots
              </div>
              {historyExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {historyExpanded && (
              <div className="border-t border-[#1F2937] p-4">
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={forecastHistory} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="marginGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#002FA7" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#002FA7" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                    <XAxis dataKey="date" tick={{ fill: "#6B7280", fontSize: 10 }} tickLine={false} />
                    <YAxis tick={{ fill: "#6B7280", fontSize: 10 }} tickLine={false} axisLine={false} unit="%" />
                    <Tooltip
                      contentStyle={{ background: "#0D1117", border: "1px solid #1F2937", borderRadius: 4, fontSize: 12 }}
                      labelStyle={{ color: "#9CA3AF" }}
                      itemStyle={{ color: "#002FA7" }}
                      formatter={(v) => [`${v}%`, "Forecast margin"]}
                    />
                    {project?.target_margin_percentage && (
                      <ReferenceLine y={project.target_margin_percentage} stroke="#F59E0B" strokeDasharray="4 4" label={{ value: `Target ${project.target_margin_percentage}%`, fill: "#F59E0B", fontSize: 10 }} />
                    )}
                    <Area type="monotone" dataKey="margin" stroke="#002FA7" fill="url(#marginGrad)" strokeWidth={2} dot={{ fill: "#002FA7", r: 3 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* ── Budget tracker ─────────────────────────────────────── */}
        <div className="border border-[#1F2937] bg-[#0D1117]" data-testid="budget-panel">
          <button
            onClick={() => setBudgetExpanded((v) => !v)}
            className="w-full h-11 flex items-center justify-between px-4 text-xs uppercase tracking-[0.25em] text-slate-400 hover:text-white hover:bg-[#111827] transition-colors"
          >
            <div className="flex items-center gap-2">
              <DollarSign className="w-3.5 h-3.5 text-[#00F5D4]" />
              Budget{budget?.budget ? ` · ${budget.percent_used ?? 0}% used` : " · not set"}
            </div>
            {budgetExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {budgetExpanded && budget && (
            <div className="border-t border-[#1F2937] p-5 space-y-4">
              {budget.budget > 0 ? (
                <>
                  {/* Burn bar */}
                  <div>
                    <div className="flex justify-between text-xs text-[#6B7280] mb-1">
                      <span>Spent: <span className="text-white font-mono">£{(budget.spent_to_date || 0).toLocaleString()}</span></span>
                      <span>Budget: <span className="text-white font-mono">£{budget.budget.toLocaleString()}</span></span>
                    </div>
                    <div className="w-full h-2 bg-[#1F2937] rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          (budget.percent_used || 0) > 90 ? "bg-[#FF007A]" :
                          (budget.percent_used || 0) > 70 ? "bg-[#F59E0B]" : "bg-[#00F5D4]"
                        }`}
                        style={{ width: `${Math.min(budget.percent_used || 0, 100)}%` }}
                      />
                    </div>
                    <div className="text-[10px] text-[#6B7280] mt-1">
                      {budget.remaining != null && (
                        budget.remaining >= 0
                          ? <span className="text-[#00F5D4]">£{budget.remaining.toLocaleString()} remaining</span>
                          : <span className="text-[#FF007A]">£{Math.abs(budget.remaining).toLocaleString()} over budget</span>
                      )}
                    </div>
                  </div>
                  {/* Breakdown */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-[#030712] border border-[#1F2937] px-3 py-2.5">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Labour</div>
                      <div className="font-mono text-sm font-bold mt-0.5">£{(budget.breakdown?.labour || 0).toLocaleString()}</div>
                    </div>
                    <div className="bg-[#030712] border border-[#1F2937] px-3 py-2.5">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Expenses</div>
                      <div className="font-mono text-sm font-bold mt-0.5">£{(budget.breakdown?.expenses || 0).toLocaleString()}</div>
                    </div>
                  </div>
                  {Object.entries(budget.breakdown?.by_expense_category || {}).length > 0 && (
                    <div className="space-y-1">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Expense breakdown</div>
                      {Object.entries(budget.breakdown.by_expense_category).map(([cat, amt]) => (
                        <div key={cat} className="flex justify-between text-xs text-[#9CA3AF]">
                          <span className="capitalize">{cat}</span>
                          <span className="font-mono">£{amt.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-3 text-sm text-[#6B7280]">
                  <span>No budget set.</span>
                  <button
                    className="text-[#00F5D4] text-xs hover:underline"
                    onClick={async () => {
                      const v = prompt("Set project budget (£):");
                      if (v && !isNaN(v)) {
                        await api.patch(`/projects/${projectId}`, { budget: parseFloat(v) });
                        loadBudget();
                        toast.success("Budget set");
                      }
                    }}
                  >
                    Set budget →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Custom fields ───────────────────────────────────────── */}
        <CustomFieldsPanel entityType="project" entityId={projectId} />

        {/* ── Milestone roadmap ───────────────────────────────────── */}
        {milestones.length > 0 && (
          <div className="border border-[#1F2937] bg-[#0D1117]" data-testid="roadmap-panel">
            <button
              onClick={() => setRoadmapExpanded((v) => !v)}
              className="w-full h-11 flex items-center justify-between px-4 text-xs uppercase tracking-[0.25em] text-slate-400 hover:text-white hover:bg-[#111827] transition-colors"
            >
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-[#F59E0B]" />
                Milestone roadmap · {milestones.length} milestone{milestones.length !== 1 ? "s" : ""}
              </div>
              {roadmapExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {roadmapExpanded && (
              <div className="border-t border-[#1F2937] px-5 py-4">
                <MilestoneRoadmap milestones={milestones} />
              </div>
            )}
          </div>
        )}

        {/* ── Activity feed ───────────────────────────────────────── */}
        <div className="border border-[#1F2937] bg-[#0D1117]" data-testid="activity-panel">
          <button
            onClick={() => setActivityExpanded((v) => !v)}
            className="w-full h-11 flex items-center justify-between px-4 text-xs uppercase tracking-[0.25em] text-slate-400 hover:text-white hover:bg-[#111827] transition-colors"
          >
            <div className="flex items-center gap-2">
              <Activity className="w-3.5 h-3.5 text-[#002FA7]" /> Activity feed
            </div>
            {activityExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {activityExpanded && (
            <div className="border-t border-[#1F2937]">
              <ActivityFeed projectId={projectId} limit={40} />
            </div>
          )}
        </div>

        <div className="border border-[#1F2937]">
          <div className="h-12 flex items-center justify-between border-b border-[#1F2937] bg-[#111827] text-white px-4">
            <div className="flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4" /> Documentation
            </div>
            <Dialog open={openManual} onOpenChange={setOpenManual}>
              <DialogTrigger asChild>
                <Button className="rounded-none h-8 px-3 bg-[#0D1117] text-white hover:bg-[#111827]" data-testid="new-manual-doc-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> New doc
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none bg-[#111827] border-[#374151] text-white sm:max-w-lg">
                <DialogHeader><DialogTitle className="font-display tracking-tight">New document</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Title</Label>
                    <Input className="rounded-none mt-1" data-testid="manual-doc-title" value={manualForm.title} onChange={(e) => setManualForm({ ...manualForm, title: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Type</Label>
                    <Input className="rounded-none mt-1" placeholder="e.g. specification, decision log" data-testid="manual-doc-type" value={manualForm.doc_type} onChange={(e) => setManualForm({ ...manualForm, doc_type: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Content (Markdown)</Label>
                    <Textarea className="rounded-none mt-1 min-h-[200px] font-mono text-sm" data-testid="manual-doc-content" value={manualForm.content} onChange={(e) => setManualForm({ ...manualForm, content: e.target.value })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={createManual} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="confirm-create-manual-doc">Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-12 min-h-[500px]">
            <div className="col-span-12 lg:col-span-4 border-r border-[#1F2937]">
              <div className="p-4 border-b border-[#1F2937] space-y-2 bg-[#030712]">
                <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280]">Generate with AI</div>
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger className="rounded-none" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AI_DOC_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={generate} disabled={generating} className="rounded-none w-full bg-[#002FA7] hover:bg-[#002480]" data-testid="generate-doc-btn">
                  <Sparkles className="w-4 h-4 mr-1" /> {generating ? "Generating…" : "Generate with AI"}
                </Button>
              </div>
              <div className="divide-y divide-[#1F2937]">
                {docs.map((d) => (
                  <button key={d.doc_id} onClick={() => { setSelectedDoc(d); setEditing(false); }} className={`w-full text-left p-4 hover:bg-[#030712] ${selectedDoc?.doc_id === d.doc_id ? "bg-[#030712] border-l-2 border-l-[#002FA7]" : "border-l-2 border-l-transparent"}`} data-testid={`doc-item-${d.doc_id}`}>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#002FA7]">{d.doc_type.replace("_", " ")}</div>
                    <div className="font-medium text-sm mt-1 line-clamp-1">{d.title}</div>
                    <div className="text-[10px] text-[#6B7280] mt-1">{new Date(d.updated_at || d.created_at).toLocaleString()}</div>
                  </button>
                ))}
                {docs.length === 0 && (
                  <div className="p-6 text-sm text-[#6B7280]">No documents yet. Generate with AI or create a manual document →</div>
                )}
              </div>
            </div>
            <div className="col-span-12 lg:col-span-8 p-8 bg-[#030712]">
              {!selectedDoc ? (
                <div className="text-sm text-[#6B7280]">Generate or create a document to preview it here.</div>
              ) : editing ? (
                <div className="bg-[#0D1117] border border-[#1F2937] p-6 max-w-3xl space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#002FA7]">{selectedDoc.doc_type.replace("_", " ")}</div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveEdit} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="save-doc-btn"><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="rounded-none" data-testid="cancel-edit-doc-btn"><X className="w-3.5 h-3.5 mr-1" /> Cancel</Button>
                    </div>
                  </div>
                  <Input
                    className="rounded-none font-display font-black text-2xl tracking-tighter border-0 px-0 h-auto focus-visible:ring-0"
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    data-testid="edit-doc-title"
                  />
                  <Textarea
                    className="rounded-none min-h-[400px] font-mono text-sm"
                    value={editForm.content}
                    onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                    data-testid="edit-doc-content"
                  />
                </div>
              ) : (
                <div className="bg-[#0D1117] border border-[#1F2937] p-8 max-w-3xl">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-[#002FA7]">{selectedDoc.doc_type.replace("_", " ")}</div>
                      <h2 className="font-display font-black text-3xl tracking-tighter mt-2">{selectedDoc.title}</h2>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={startEdit} className="rounded-none" data-testid="edit-doc-btn"><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="outline" onClick={removeDoc} className="rounded-none border-[#374151] text-gray-500 hover:text-red-600" data-testid="delete-doc-btn"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                  <div className="mt-6 text-sm text-slate-100">
                    <SimpleMD text={selectedDoc.content} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border border-[#1F2937]">
          <div className="h-12 flex items-center justify-between border-b border-[#1F2937] bg-[#111827] text-white px-4">
            <div className="flex items-center gap-2 text-sm">
              <ClipboardList className="w-4 h-4" /> PMBOK Documents
            </div>
            <Dialog open={pmpPickerOpen} onOpenChange={setPmpPickerOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-none h-8 px-3 bg-[#0D1117] text-white hover:bg-[#111827]" data-testid="generate-pmp-docs-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Generate from template
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none bg-[#111827] border-[#374151] text-white sm:max-w-lg">
                <DialogHeader><DialogTitle className="font-display tracking-tight">Generate PMBOK documents</DialogTitle></DialogHeader>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-[#00F5D4]">Choose templates</Label>
                  {pmpSelected.length > 0 && <span className="text-[11px] text-[#6B7280]">{pmpSelected.length} selected</span>}
                </div>
                <PmpTemplatePicker selected={pmpSelected} onChange={setPmpSelected} />
                <DialogFooter>
                  <Button onClick={generatePmpDocs} disabled={pmpBusy} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="confirm-generate-pmp-docs">
                    {pmpBusy ? "Generating…" : "Generate"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {pmpDocs.length === 0 ? (
            <div className="p-6 text-sm text-[#6B7280]">No PMBOK documents yet. Generate from template to seed blank, editable placeholders (Charter, Scope Statement, WBS, Risk Register, etc.) →</div>
          ) : (
            <div className="divide-y divide-[#1F2937]">
              {pmpDocs.map((d) => (
                <div key={d.doc_id} className="flex items-center justify-between p-4" data-testid={`pmp-doc-${d.doc_id}`}>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#002FA7]">{d.process_group}</div>
                    <div className="font-medium text-sm mt-1 truncate">{d.name}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => downloadPmpDoc(d)} className="rounded-none" data-testid={`download-pmp-doc-${d.doc_id}`}><Download className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" onClick={() => deletePmpDoc(d)} className="rounded-none border-[#374151] text-gray-500 hover:text-red-600" data-testid={`delete-pmp-doc-${d.doc_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Close-out dialog */}
      <Dialog open={closeOutOpen} onOpenChange={setCloseOutOpen}>
        <DialogContent className="rounded-none border-[#1F2937] bg-[#111827] text-white sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-white flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-[#F59E0B]" /> Close out project
            </DialogTitle>
            <p className="text-xs text-[#6B7280] mt-1">
              Record the final outcome. This banks clean training data for future margin predictions. The overrun cause is optional.
            </p>
          </DialogHeader>

          <div className="space-y-4 pt-1">
            {/* Row 1 — financials */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">Final contract value (£)</Label>
                <Input
                  type="number" min="0" step="100"
                  className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white"
                  value={closeForm.final_contract_value}
                  onChange={(e) => setCloseForm({ ...closeForm, final_contract_value: e.target.value })}
                  onBlur={computeMargin}
                  data-testid="closeout-contract-value"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">Final cost (£)</Label>
                <Input
                  type="number" min="0" step="100"
                  className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white"
                  value={closeForm.final_cost}
                  onChange={(e) => setCloseForm({ ...closeForm, final_cost: e.target.value })}
                  onBlur={computeMargin}
                  data-testid="closeout-final-cost"
                />
              </div>
            </div>

            {/* Row 2 — cost split */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">Labour cost (£)</Label>
                <Input
                  type="number" min="0" step="100"
                  className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white"
                  value={closeForm.final_labour_cost}
                  onChange={(e) => setCloseForm({ ...closeForm, final_labour_cost: e.target.value })}
                  data-testid="closeout-labour-cost"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">Non-labour cost (£)</Label>
                <Input
                  type="number" min="0" step="100"
                  className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white"
                  value={closeForm.final_nonlabour_cost}
                  onChange={(e) => setCloseForm({ ...closeForm, final_nonlabour_cost: e.target.value })}
                  data-testid="closeout-nonlabour-cost"
                />
              </div>
            </div>

            {/* Row 3 — margin */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">Final margin (%)</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="number" min="-100" max="100" step="0.1"
                    className="rounded-none bg-[#030712] border-[#374151] text-white flex-1"
                    value={closeForm.final_margin}
                    onChange={(e) => setCloseForm({ ...closeForm, final_margin: e.target.value })}
                    data-testid="closeout-final-margin"
                  />
                  <Button
                    type="button" size="sm"
                    className="rounded-none bg-[#1F2937] border border-[#374151] text-[#9CA3AF] hover:text-white text-xs px-2"
                    onClick={computeMargin}
                  >Calc</Button>
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">Target margin (%)</Label>
                <Input
                  type="number" min="0" max="100" step="1"
                  className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white"
                  value={closeForm.target_margin}
                  onChange={(e) => setCloseForm({ ...closeForm, target_margin: e.target.value })}
                  data-testid="closeout-target-margin"
                />
              </div>
            </div>

            {/* Row 4 — schedule + scope */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">Days over schedule</Label>
                <Input
                  type="number" step="1"
                  placeholder="0"
                  className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white"
                  value={closeForm.days_over_schedule}
                  onChange={(e) => setCloseForm({ ...closeForm, days_over_schedule: e.target.value })}
                  data-testid="closeout-days-over"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">Scope changes (#)</Label>
                <Input
                  type="number" min="0" step="1"
                  placeholder="0"
                  className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white"
                  value={closeForm.total_scope_changes}
                  onChange={(e) => setCloseForm({ ...closeForm, total_scope_changes: e.target.value })}
                  data-testid="closeout-scope-changes"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">Change order value (£)</Label>
                <Input
                  type="number" min="0" step="100"
                  placeholder="0"
                  className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white"
                  value={closeForm.total_change_value}
                  onChange={(e) => setCloseForm({ ...closeForm, total_change_value: e.target.value })}
                  data-testid="closeout-change-value"
                />
              </div>
            </div>

            {/* Overrun cause — optional */}
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">
                Overrun cause <span className="text-[#4B5563] normal-case tracking-normal">(optional)</span>
              </Label>
              <Select value={closeForm.overrun_cause} onValueChange={(v) => setCloseForm({ ...closeForm, overrun_cause: v })}>
                <SelectTrigger className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="closeout-overrun-cause">
                  <SelectValue placeholder="— none / delivered on target —" />
                </SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#374151] text-white">
                  <SelectItem value="none">— none / delivered on target —</SelectItem>
                  <SelectItem value="scope_creep">Scope creep</SelectItem>
                  <SelectItem value="underpricing">Underpricing</SelectItem>
                  <SelectItem value="rework">Rework / quality issues</SelectItem>
                  <SelectItem value="client_delay">Client delay</SelectItem>
                  <SelectItem value="estimation_error">Estimation error</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Live margin preview */}
            {closeForm.final_margin !== "" && closeForm.target_margin !== "" && (
              <div className={`px-4 py-3 border font-mono text-sm ${
                parseFloat(closeForm.final_margin) >= parseFloat(closeForm.target_margin) - 5
                  ? "border-[#00F5D4]/30 bg-[#00F5D4]/5 text-[#00F5D4]"
                  : parseFloat(closeForm.final_margin) >= parseFloat(closeForm.target_margin) - 15
                  ? "border-[#F59E0B]/30 bg-[#F59E0B]/5 text-[#F59E0B]"
                  : "border-[#FF007A]/30 bg-[#FF007A]/5 text-[#FF007A]"
              }`}>
                Final margin {closeForm.final_margin}% vs target {closeForm.target_margin}% —{" "}
                {parseFloat(closeForm.final_margin) >= parseFloat(closeForm.target_margin) - 5
                  ? "✓ on target"
                  : parseFloat(closeForm.final_margin) >= parseFloat(closeForm.target_margin) - 15
                  ? "⚠ warning breach"
                  : "✗ critical breach"}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="rounded-none border-[#374151] text-[#9CA3AF]"
              onClick={() => setCloseOutOpen(false)}
            >Cancel</Button>
            <Button
              onClick={submitCloseOut}
              disabled={closeOutBusy}
              className="rounded-none bg-[#F59E0B] hover:bg-[#D97706] text-[#030712] font-bold"
              data-testid="closeout-confirm"
            >
              {closeOutBusy ? "Saving…" : "Record outcome"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetail;

const MilestoneRoadmap = ({ milestones }) => {
  const today = new Date().toISOString().slice(0, 10);
  // Group by month
  const byMonth = {};
  for (const m of milestones) {
    const key = m.due_date?.slice(0, 7) || "unknown";
    (byMonth[key] = byMonth[key] || []).push(m);
  }
  const months = Object.entries(byMonth).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="relative pl-6">
      {/* Vertical spine */}
      <div className="absolute left-2 top-0 bottom-0 w-px bg-[#1F2937]" />
      <div className="space-y-6">
        {months.map(([month, items]) => (
          <div key={month}>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] mb-2 font-mono">
              {month !== "unknown" ? new Date(month + "-01T00:00:00Z").toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" }) : "No date"}
            </div>
            <div className="space-y-2">
              {items.map((m) => {
                const overdue = m.due_date < today && m.status !== "done";
                const done = m.status === "done";
                return (
                  <div key={m.task_id} className="flex items-center gap-3 group">
                    {/* Node */}
                    <div className={`w-3.5 h-3.5 rotate-45 flex-shrink-0 border ${
                      done ? "bg-[#00F5D4] border-[#00F5D4]" :
                      overdue ? "bg-[#FF007A]/20 border-[#FF007A]" :
                      "bg-[#F59E0B]/10 border-[#F59E0B]"
                    }`} style={{ marginLeft: -5 }} />
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm ${done ? "line-through text-[#6B7280]" : overdue ? "text-[#FF007A]" : "text-white"}`}>
                        {m.title}
                      </span>
                    </div>
                    <span className={`text-[10px] font-mono flex-shrink-0 ${
                      done ? "text-[#00F5D4]" : overdue ? "text-[#FF007A]" : "text-[#6B7280]"
                    }`}>
                      {m.due_date}
                    </span>
                    {done && <span className="text-[10px] text-[#00F5D4]">✓</span>}
                    {overdue && !done && <span className="text-[10px] text-[#FF007A]">late</span>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProjectPresenceEyebrow = ({ methodology, projectId }) => {
  const { viewersForProject, selfConnectionId } = usePresence();
  const all = viewersForProject(projectId);
  const others = all.filter((u) => u.connection_id !== selfConnectionId);
  return (
    <span className="flex items-center gap-3">
      <span>Project · {methodology}</span>
      {others.length > 0 && (
        <span
          className="inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
          title={others.map((u) => u.name).join(", ")}
          data-testid="project-presence-pill"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          {others.length} viewing
        </span>
      )}
    </span>
  );
};

const ForecastStat = ({ label, value, accent }) => (
  <div className="bg-[#030712] border border-[#1F2937] px-3 py-2.5">
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">{label}</div>
    <div className={`text-lg font-bold mt-0.5 ${accent || "text-white"}`}>{value}</div>
  </div>
);
